<template>
  <IngredientsCartModal
    v-if="showIngredientsCartModal"
    :type="ingredientCartType"
    :plants="plants"
    @closeModal="showIngredientsCartModal = false"
  />
  <CopyIngredientsModal
    v-if="copyIngredientsModalVisible"
    :plants="plants"
    :ingredientIds="selectedIngredientIds"
    @closeModal="onCopyModalClosed"
  />
  <DeletionModal
    v-if="deletionModalVisible"
    :plants="plants"
    :ingredientIds="selectedIngredientIds"
    @closeModal="onDeletionModalClosed"
  />
  <DownloadsModal
    v-if="showDownloadsModal"
    :plants="plants"
    @closeModal="showDownloadsModal = false"
  />

  <div class="sched-a-mobile-view">
    <section class="search-section" data-cy="search-section">
      <div class="search-wrapper">
        <TableSearch
          class="product-mobile-search"
          :value="searchInput"
          placeholder="Search by Name and/or Brand and/or Plant"
          @searchSubmitted="onSearchSubmitted"
        />
        <span
          class="filter-icon"
          @click="showFilters = true"
          data-cy="filters-button"
        >
          <FilterIcon />
        </span>
      </div>
    </section>

    <IngredientsFiltersMenu
      v-if="showFilters"
      :getPropValues="getPropValuesAsync"
      :filters="ingredientFilters"
      @apply="onFiltersApplied"
      @clear="onFiltersCleared"
      @close="showFilters = false"
    />

    <div class="sort-wrapper">
      <DropDownList
        class="sort-dropdown"
        :textField="'text'"
        :data-items="sortOptions"
        v-model="selectedSortOption"
        @change="onSortChanged"
        data-cy="sort-dropdown"
      />
    </div>

    <section class="items-section">
      <header class="items-sort" v-if="canOpenOrModifyCarts">
        <div class="action-island">
          <Button
            v-if="canOpenOrModifyCarts"
            class="add-ingredients"
            type="button"
            :disabled="cartActions[0].count === 0"
            @click="cartActions[0].onClick"
          >
            <AddCartIcon />
            {{ cartActions[0].count }}
          </Button>

          <Button
            v-if="canOpenOrModifyCarts"
            class="del-ingredients"
            type="button"
            :disabled="cartActions[1].count === 0"
            @click="cartActions[1].onClick"
          >
            <DeleteCartIcon />
            {{ cartActions[1].count }}
          </Button>

          <Button
            class="download"
            type="button"
            :loading="!viewLoaded"
            @click="showDownloadsModal = true"
          >
            <DownloadIcon style="fill: white" />
          </Button>
        </div>
      </header>

      <div v-if="canOpenOrModifyCarts" class="button-island">
        <Button
          type="button"
          theme-color="primary"
          class="add-ingredients"
          :disabled="!selectedIngredientIds.length"
          @click="onCopyClicked"
          data-cy="add-ingredients-button"
          >Add Selected Ingredients</Button
        >
        <Button
          type="button"
          theme-color="error"
          class="del-ingredients"
          :disabled="!selectedIngredientIds.length"
          @click="onDeletionClicked"
          data-cy="delete-ingredients-button"
          >Delete Selected Ingredients</Button
        >
      </div>

      <section v-if="canOpenOrModifyCarts" class="new-ingredient">
        <div class="trigger" @click="viewNew = true" v-if="!viewNew">+</div>
        <IngredientApplicationModal
          v-if="viewNew"
          :plants="plants"
          @closeModal="viewNew = false"
        />
      </section>
    </section>

    <IngredientList
      :ingredients="ingredients"
      :totalIngredients="totalIngredients"
      :defaultTake="ingredientsTake"
      :skip="ingredientsSkip"
      :loading="ingredientsLoading"
      :canModifyCarts="canOpenOrModifyCarts"
      @ingredientsChanged="onIngredientsChanged"
    />
  </div>
</template>

<script setup lang="ts">
import { api } from "@/api";
import { EnumDpm } from "@/Domain/EnumDpm";
import DeletionModal from "./DeletionModal.vue";
import DownloadsModal from "./DownloadsModal.vue";
import IngredientList from "./IngredientList.vue";
import AddCartIcon from "@/icons/AddCartIcon.vue";
import FilterIcon from "@/icons/filterIconAlt.vue";
import { computed, onBeforeMount, ref } from "vue";
import DownloadIcon from "@/icons/DownloadIcon.vue";
import enumHelper from "@/shared/helpers/enumHelper";
import { useMainStore } from "@/stores/useMainStore";
import { Button } from "@progress/kendo-vue-buttons";
import DeleteCartIcon from "@/icons/DeleteCartIcon.vue";
import IngredientsCartModal from "./IngredientsCartModal.vue";
import { DropDownList } from "@progress/kendo-vue-dropdowns";
import TableSearch from "@/shared/ui/inputs/TableSearch.vue";
import CopyIngredientsModal from "./CopyIngredientsModal.vue";
import { CacheService } from "@/shared/services/CacheService";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { EnumCompanyStatus } from "@/Domain/EnumCompanyStatus";
import { EnumUsedIn1Status } from "@/Domain/EnumUsedIn1Status";
import { useScheduleAStore } from "@/stores/useScheduleAStore";
import { IngredientsAppGroup } from "@/shared/access/RoleGroups";
import { SubApplicationType } from "@/Domain/SubApplicationType";
import IngredientsFiltersMenu from "./IngredientsFiltersMenu.vue";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import IngredientApplicationModal from "./IngredientApplicationModal.vue";
import { EnumFilterStatementConnector } from "@/Domain/EnumFilterStatementConnector";
import {
  boolYNConvert,
  FilterValuesTake,
  valueFilterConvert,
} from "@/shared/helpers/filtersHelper";
import type { PlantWithSelection } from "@/api/scheduleB";
import type { IdAndName } from "@/Domain/IdAndName";
import type { FilterGroup } from "@/Domain/FilterGroup";
import type { FilterField } from "@/Domain/FilterField";
import type { FilterMenuItem } from "@/Domain/FilterMenuItem";
import type { FilterExpression } from "@/Domain/FilterExpression";
import type { ValueFieldConvertOption } from "@/Domain/ValueFieldConvertOption";
import { convertNumber } from "@/Domain/EnumGroupConverter";

import type {
  IngredientWithSelection,
  GetIngredientDisplayScheduleAWithFilteringRequest,
} from "@/api/scheduleA";

const searchInput = ref("");
const viewNew = ref(false);

const main = useMainStore();
const scheduleStore = useScheduleAStore();
const plants = ref<IdAndName[]>([]);
const viewLoaded = ref(false);
const showDownloadsModal = ref(false);
const showIngredientsCartModal = ref(false);
const ingredientCartType = ref<SubApplicationType>(SubApplicationType.Add);

const deletionModalVisible = ref<boolean>(false);
const copyIngredientsModalVisible = ref<boolean>(false);

const ingredients = ref<IngredientWithSelection[]>([]);
const totalIngredients = ref<number>(0);
const ingredientsLoading = ref<boolean>(false);
const ingredientsTake = ref<number>(100);
const ingredientsSkip = ref<number>(0);
const showFilters = ref<boolean>(false);
const ingredientFilters = ref<FilterMenuItem[]>([]);
const sortAscending = ref<boolean>(true);

const sortOptions = ref([
  {
    text: "Sort By",
    value: "",
  },
  {
    text: "Label Name",
    value: "VueScheduleALabelName",
  },
  {
    text: "Plant Name",
    value: "PlantName",
  },
  {
    text: "Brand Name",
    value: "VueScheduleABrandName",
  },
  {
    text: "Source Company",
    value: "DistributorCompanyName",
  },
  {
    text: "Date Added",
    // value: "DateAdded",
    value: "DateTimeAdded",
  },
]);

if (main.isEmulator) {
  sortOptions.value.push({
    text: "Expiration Date",
    value: "CertifiedUntil",
  });
}

const selectedSortOption = ref<{ text: string; value: string }>(
  sortOptions.value[0]
);

const canOpenOrModifyCarts = computed(() => {
  return (
    (main.companySummary &&
      main.companySummary.denials.every(
        (d) => !d.IA || d.IA.toUpperCase() !== "ALL"
      ) &&
      IngredientsAppGroup.roles.some((r) => main.isInRole(r))) ??
    false
  );
});

const cartActions = ref([
  {
    name: "Complete order: Add Ingredients",
    count: scheduleStore.getCartCount(SubApplicationType.Add),
    subApplication: SubApplicationType.Add,
    onClick: () => {
      ingredientCartType.value = SubApplicationType.Add;
      showIngredientsCartModal.value = true;
    },
  },
  {
    name: "Complete order: Delete Ingredients",
    count: scheduleStore.getCartCount(SubApplicationType.Terminate),
    subApplication: SubApplicationType.Terminate,
    onClick: () => {
      ingredientCartType.value = SubApplicationType.Terminate;
      showIngredientsCartModal.value = true;
    },
  },
]);

const selectedIngredientIds = computed(() => {
  return ingredients.value
    .filter((ingredient) => ingredient.selected)
    .map((ingredient) => ingredient.UsedInOneId);
});

const valueFiltersConvertOptions = [
  {
    column: "_Dpm",
    convert: (value: number) => {
      return EnumDpm[value];
    },
  },
  {
    column: "_Pesach",
    convert: boolYNConvert,
  },
  {
    column: "VueScheduleAUsedInOneStatus",
    convert: (value: number) => {
      return enumHelper.splitByCapitalLetter(EnumUsedIn1Status[value]);
    },
  },
  {
    column: "VueScheduleAGroup",
    convert: (value: number) => {
      return convertNumber(value);
    },
  },
] as ValueFieldConvertOption[];

const getIngredientDisplayScheduleAWithFiltering = async function (
  valuesForFieldOnly: string = ""
) {
  const filterModel = {
    FilterGroups: [],
    BooleanLogicOperator: EnumFilterStatementConnector.And,
  } as FilterExpression;

  if (!valuesForFieldOnly) {
    ingredientFilters.value.forEach((f) => {
      const filterGroup = {
        FilterFields: [],
        BooleanLogicOperator: EnumFilterStatementConnector.And,
        FieldName: f.field,
        InternalFieldName: "",
      } as FilterGroup;

      if (f.value === "Both") return;

      const filterField = {
        Operator: f.getOpValue ? f.getOpValue(f.value) : "eq",
        Value: f.getFilterValue ? f.getFilterValue(f.value) : f.value,
      } as FilterField;

      filterGroup.FilterFields.push(filterField);
      filterModel.FilterGroups.push(filterGroup);
    });
  }

  const requestParams = {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    searchString: searchInput.value,
    plantList: plants.value.map((p) => p.ID).join(";"),
    allPlantCompanies: false,
    maxInstancesToReturn: 0,
    filters: JSON.stringify(filterModel),
    take: ingredientsTake.value,
    skip: ingredientsSkip.value,
    sortField: selectedSortOption.value.value,
    ascending: sortAscending.value,
  } as GetIngredientDisplayScheduleAWithFilteringRequest;

  if (!valuesForFieldOnly)
    return api.get("GetIngredientDisplayScheduleAWithFiltering", requestParams);

  requestParams.valuesForFieldOnly = valuesForFieldOnly;
  requestParams.take = FilterValuesTake;
  requestParams.skip = 0;

  return CacheService.getAsync(
    createCacheKey(requestParams, "GetIngredientDisplayScheduleAWithFiltering"),
    () => api.get("GetIngredientDisplayScheduleAWithFiltering", requestParams)
  );
};

const getIngredientsAsync = async function (
  appendIngredients: boolean = false
) {
  if (ingredientsLoading.value) return;

  ingredientsLoading.value = true;

  await handleResponsePromiseAsync(
    getIngredientDisplayScheduleAWithFiltering(),
    (data) => {
      if (!appendIngredients) ingredients.value = data.ListItems;
      else ingredients.value = [...ingredients.value, ...data.ListItems];
      totalIngredients.value = data.TotalItemsInList;
    },
    "ScheduleAMobileView.vue",
    "getIngredientsAsync"
  );

  ingredientsLoading.value = false;
};

const getPlantsAsync = async function () {
  return api.get("GetAuthorizedCertifiedOrPendingOwns", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
    certifiedOnly: false,
  });
};

const updatePlantListAsync = async function () {
  return handleResponsePromiseAsync(
    getPlantsAsync(),
    (data) => {
      plants.value = data.filter((p) => p.Status !== EnumCompanyStatus.Special);
    },
    "ScheduleAMobileView.vue",
    "updatePlantListAsync"
  );
};

const getPropValuesAsync = async function (field: string) {
  let items: IngredientWithSelection[] = [];
  await handleResponsePromiseAsync(
    getIngredientDisplayScheduleAWithFiltering(field),
    (data) => {
      console.debug(
        `ScheduleAMobileView.vue: getPropValuesAsync(), result:`,
        data
      );
      items = data.ListItems;
      valueFilterConvert(valueFiltersConvertOptions, field!, items);
    },
    "ScheduleAMobileView.vue",
    "getPropValuesAsync"
  );
  return items;
};

const updateIngredientsInCartCountAsync = async function () {
  const addAction = cartActions.value.find(
    (action) => action.subApplication === SubApplicationType.Add
  )!;
  const terminateAction = cartActions.value.find(
    (action) => action.subApplication === SubApplicationType.Terminate
  )!;

  const cartPromise = api.get("VueGetIngCartCount", {
    userLogin: main.userLogin,
    companyLogin: main.companyLogin,
  });

  await handleResponsePromiseAsync(
    cartPromise,
    (data) => {
      scheduleStore.setCartCount(addAction.subApplication, data.adds);
      scheduleStore.setCartCount(terminateAction.subApplication, data.deletes);
    },
    "ScheduleAMobileView.vue",
    "updateIngredientsInCartCountAsync"
  );
};

const onCopyClicked = function () {
  (plants.value as PlantWithSelection[]).forEach((p) => (p.selected = false));
  copyIngredientsModalVisible.value = true;
};

const onDeletionClicked = function () {
  deletionModalVisible.value = true;
};

const onCopyModalClosed = function (cartCount: number) {
  copyIngredientsModalVisible.value = false;
  if (cartCount > -1) {
    clearSelection();
  }
};

const onDeletionModalClosed = function (cartCount: number) {
  deletionModalVisible.value = false;
  if (cartCount > -1) {
    clearSelection();
  }
};

const clearSelection = function () {
  ingredients.value.forEach((p) => (p.selected = false));
};

const onCartCountUpdate = function (type: SubApplicationType, count: number) {
  const action = cartActions.value.find(
    (action) => action.subApplication === type
  );
  if (action) {
    action.count = count;
  }
};

const onSearchSubmitted = function (searchValue: string) {
  searchInput.value = searchValue;
  ingredientsSkip.value = 0;
  getIngredientsAsync();
};

const onIngredientsChanged = function (skip: number, take: number) {
  ingredientsTake.value = take;
  ingredientsSkip.value = skip;
  getIngredientsAsync(true);
};

const onFiltersApplied = function (filters: FilterMenuItem[]) {
  ingredientFilters.value = filters;
  ingredientsSkip.value = 0;
  getIngredientsAsync();
};

const onFiltersCleared = function () {
  ingredientFilters.value = [];
  ingredientsSkip.value = 0;
  getIngredientsAsync();
};

const onSortChanged = function () {
  ingredientsSkip.value = 0;
  getIngredientsAsync();
};

scheduleStore.handleCartCountUpdate = onCartCountUpdate;

onBeforeMount(async () => {
  updateIngredientsInCartCountAsync();
  await updatePlantListAsync();
  await getIngredientsAsync();
});
</script>

<style scoped lang="scss">
.sched-a-mobile-view {
  flex: 1 0 auto;
  background: #f8fafb;
}
.search-section {
  background: #222;
  border-top: 1px solid #333;
  display: flex;
  align-items: center;
  padding: 10px 15px;

  .sched-a-search {
    flex: 1;
    overflow: hidden;
  }

  .search-wrapper {
    position: relative;
    display: flex;
    flex: 1;

    .product-mobile-search {
      width: 100%;
    }
  }

  .filter-icon {
    cursor: pointer;
    position: absolute;
    right: 0px;
    height: 100%;
    top: 0px;
    background: white;
    padding: 4px 7px 0;
  }
}

// items
.items-section {
  padding: 20px 15px;
}
.items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

//actions
.action-island {
  margin-left: auto;
  display: flex;
  gap: 10px;
  > * {
    background: #444;
    color: white;
    cursor: pointer;
    border-radius: 4px;
    padding: 5px 15px;
    flex: 1;
    display: flex;
    gap: 5px;
    font-size: 12px;
    align-items: center;
    &.sort-wrapper {
      background: white;
      color: black;
      flex-basis: 50%;
      flex-grow: 0;
      margin-top: 10px;
    }
    &.add-ingredients {
      background: #428bca; // #008a00;
    }
    &.del-ingredients {
      background: #d9534f; // #c0240f;
    }
  }
  &.download {
    margin-top: 10px;
    margin-bottom: 10px;
    color: white;
  }
}

.button-island {
  margin-left: auto;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;

  > * {
    background: #444;
    color: white;
    cursor: pointer;
    border-radius: 4px;
    padding: 5px 15px;
    flex: 1;
    display: flex;
    gap: 5px;
    font-size: 12px;
    align-items: center;
    &.disabled {
      opacity: 0.4;
    }
    &.add-ingredients {
      margin-top: 10px;
      margin-bottom: 10px;
      background: #428bca; // blue;
    }
    &.del-ingredients {
      margin-top: 10px;
      margin-bottom: 10px;
      background: #d9534f; // red;
    }
  }
}

//new-ingredient
.new-ingredient {
  .trigger {
    position: fixed;
    bottom: 20px;
    right: 10px;
    background: #428bca;
    color: white;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    font-weight: 200;
    cursor: pointer;
    box-shadow: 0 0 15px rgba(0, 0, 0, 0.2);
    z-index: 1;
    &:active {
      background: #204f77;
    }
  }
  .new-ingredient-screen {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 99999;
    background: white;
    > header {
      background: #222;
      color: white;
      font-weight: bold;
      padding: 10px 15px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    > nav {
      border-bottom: 1px solid #ddd;
      padding: 0px 15px 0;
      display: flex;
      gap: 0;
      background: #efefef;
    }
    > nav > span {
      cursor: pointer;
      display: inline-block;
      padding: 10px 15px;
      margin-bottom: -1px;
      border-bottom: 2px solid transparent;
      &.active {
        border-color: #428bca;
        color: #428bca;
      }
    }
    > main {
      padding: 10px 25px;
    }
    button {
      border: none;
      border-radius: 6px;
      padding: 8px 12px;
      background: #428bca;
      color: white;
      flex: 3;
      border: 1px solid transparent;
    }
  }
}
</style>
